'use client';

import { useEffect, useState } from 'react';
import { Card, CardContent, CardTitle } from '../../components/ui/card.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Avatar, AvatarImage } from '../../components/ui/avatar.tsx';
import { ArrowRight, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useGetUpcomingEvents,
  useUpdateEvent,
  useGetUpcomingEventsV2,
} from '../../api/events/eventQueries.js';
import useAuth from '../../hooks/useAuth.js';

export default function RecentEvents() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    data: allEvents,
    isLoading,
    isFetched,
  } = useGetUpcomingEventsV2(user.staffId);
  const { mutate: updateEvent } = useUpdateEvent();
  const [featuredEvent, setFeaturedEvent] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    if (isFetched) {
      setFeaturedEvent(allEvents.data[0]);
      console.log('All Events:', allEvents.data);
      setUpcomingEvents(allEvents.data.slice(1));
    }
  }, [isFetched, allEvents]);

  // Get the first event as the featured event and the rest as upcoming events

  const handleViewDetails = (event) => {
    console.log('View Details:', event);
    const eventId = `${event.eventTitle
      .toLowerCase()
      .replace(/\s+/g, '-')}-${event.eventDate
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')}`;
    navigate(`/events/${eventId}`);
  };

  const handleAddToCalendar = (event) => {
    updateEvent({ id: event.id, data: { participants: [{ id: user.id }] } });
    navigate('/events/calendar');
  };

  if (isLoading) {
    return (
      <div className="w-full">
        <CardTitle className="text-base font-semibold mb-4">
          Noticeboard
        </CardTitle>
        <div className="bg-white rounded-lg p-6 text-center text-gray-500">
          Loading events...
        </div>
      </div>
    );
  }

  if (!featuredEvent) {
    return (
      <div className="w-full">
        <CardTitle className="text-base font-semibold mb-4">
          Noticeboard
        </CardTitle>
        <div className="bg-white rounded-lg p-6 text-center text-gray-500">
          No upcoming events
        </div>
      </div>
    );
  }

  return (
    <div className="w-full transition-all duration-200 ease-in-out">
      <CardTitle className="text-base font-semibold mb-6">
        Noticeboard
      </CardTitle>

      <div className="space-y-6">
        {/* Featured Event Card */}
        <div className="bg-pink-50/80 rounded-xl p-5 transition-all duration-200 hover:shadow-md border border-purple-100">
          <div className="flex items-center gap-2 text-purple-600 mb-4">
            <Star className="h-4 w-4 fill-purple-600" />
            <span className="text-sm font-medium">Featured Event</span>
          </div>

          {/* Profile */}
          <div className="flex items-center gap-3 mb-4">
            <Avatar className="h-10 w-10 shrink-0 ring-2 ring-purple-100 bg-purple-50">
              <AvatarImage
                src={
                  featuredEvent.attendees?.[0]?.image || '/avatars/default.png'
                }
                alt={featuredEvent.attendees?.[0]?.name}
                className="object-cover"
              />
            </Avatar>
            <div className="min-w-0">
              <h3 className="font-medium text-sm text-gray-700 truncate">
                {featuredEvent.attendees?.[0]?.name}
              </h3>
              <p className="text-xs text-gray-500 truncate">
                Event Coordinator
              </p>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <h4 className="font-semibold text-base text-gray-900 truncate">
              {featuredEvent.title}
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">
              {featuredEvent.description}
            </p>
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <span className="font-medium">{featuredEvent.displayDate}</span>
              <span className="h-1 w-1 rounded-full bg-gray-300" />
              <span>{featuredEvent.time}</span>
            </p>
          </div>

          <div className="sm:items-center gap-4">
            <div className="min-w-0 sm:pr-6">
              <div className="flex flex-col items-center gap-1.5">
                <div className="flex -space-x-3">
                  {featuredEvent.attendees
                    ?.slice(0, 3)
                    .map((attendee, index) => (
                      <Avatar
                        key={index}
                        className="h-6 w-6 border-2 border-white bg-purple-50 ring-0"
                      >
                        <AvatarImage
                          src={attendee.image || '/avatars/default.png'}
                          alt={attendee.name}
                          className="object-cover"
                        />
                      </Avatar>
                    ))}
                  {featuredEvent.attendees?.length > 3 && (
                    <div className="h-6 w-6 rounded-full bg-purple-100 border-2 border-white flex items-center justify-center">
                      <span className="text-xs text-purple-600 font-medium">
                        +{featuredEvent.attendees.length - 3}
                      </span>
                    </div>
                  )}
                </div>
                <span className="text-[11px] text-gray-400 font-medium">
                  {featuredEvent.comments} Comments
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:ml-auto shrink-0 mt-3">
              <Button
                variant="outline"
                size="sm"
                className="whitespace-nowrap text-purple-600 border-purple-200 hover:bg-purple-50"
                onClick={() => handleViewDetails(featuredEvent)}
              >
                View
              </Button>
              <Button
                className="whitespace-nowrap bg-purple-600 hover:bg-purple-700"
                size="sm"
                onClick={() => handleAddToCalendar(featuredEvent)}
              >
                Add to calendar
              </Button>
            </div>
          </div>
        </div>

        {/* Event Cards Grid */}
        {upcomingEvents.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            {upcomingEvents.map((event, i) => (
              <Card
                key={i}
                className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                  event.type === 'sports'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white'
                }`}
                onClick={() => handleViewDetails(event)}
              >
                <CardContent className="p-4">
                  <div className="min-h-[100px] flex flex-col justify-between min-w-0">
                    <div className="min-w-0">
                      <h4 className="font-medium text-sm mb-1.5 truncate">
                        {event.title}
                      </h4>
                      <p
                        className={`text-sm line-clamp-2 ${
                          event.type === 'sports'
                            ? 'text-purple-100'
                            : 'text-gray-600'
                        }`}
                      >
                        {event.description}
                      </p>
                    </div>
                    <div className="flex items-center justify-between mt-3 gap-2">
                      <span
                        className={`text-xs truncate ${
                          event.type === 'sports'
                            ? 'text-purple-200'
                            : 'text-gray-500'
                        }`}
                      >
                        {event.displayDate}
                      </span>
                      <ArrowRight
                        className={`h-4 w-4 shrink-0 ${
                          event.type === 'sports'
                            ? 'text-purple-200'
                            : 'text-gray-400'
                        }`}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
